import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { InvoiceLineType } from '../../../../../../../data/models/_generatedUblInvoice'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../../../ui-config'
import { LinesView } from './lines.view'

export type FormValues = {
  invoiceLines?: InvoiceLineType[]
}
export type FormFieldName = keyof InvoiceLineType

type Props = {
  invoiceLines?: InvoiceLineType[]
  isLoading: boolean
  editable?: boolean
  hasLoadingRowId: (rowId: string) => boolean
  handleSave: (formData: InvoiceLineType) => Promise<void>
}

export const LinesForm: React.FC<Props> = ({
  invoiceLines,
  isLoading,
  editable = false,
  hasLoadingRowId,
  handleSave,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  const deepCopyInvoiceLines = invoiceLines
    ? JSON.parse(JSON.stringify(invoiceLines))
    : []

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      invoiceLines: deepCopyInvoiceLines,
    },
    validate: formData => {
      let errors: any = []

      const lineIds = formData.invoiceLines?.map(line => line.LineId) || []
      const duplicates = lineIds.filter(
        (item, index) => lineIds.indexOf(item) !== index,
      )

      formData.invoiceLines?.forEach((data, index) => {
        if (!errors[index]) {
          errors[index] = {}
        }

        const fieldsToValidate: FieldToValidate[] = [
          {
            fieldName: 'OrderLineReference.LineID.Value',
            config: config.eInvoice.additionalData.invoiceLines.lineId,
          },
          {
            fieldName: 'Item.BuyersItemIdentification.ID.Value.Value',
            config: config.eInvoice.additionalData.invoiceLines.buyersItemId,
          },
        ]

        validateFormikFields(fieldsToValidate, data, errors[index])

        if (
          data.OrderLineReference?.LineID?.IsEditEnabled &&
          !data.OrderLineReference?.LineID?.Value
        ) {
          errors[index] = {
            ...errors[index],
            LineID: t(
              'eInvoiceDetails.stepper.additionalData.positions.table.columns.orderPosition.errors.required',
            ),
          }
        } else if (duplicates?.includes(data.LineId)) {
          errors[index] = {
            ...errors[index],
            LineID: t(
              'eInvoiceDetails.stepper.additionalData.positions.table.columns.orderPosition.errors.duplicate',
            ),
          }
        }
      })

      return errors
    },
    onSubmit: async formData => {},
  })

  const isFormFieldValid = (index: number, name: FormFieldName) =>
    !(
      // Array.isArray(formik.touched.invoiceLines) &&
      // formik.touched.invoiceLines[index]?.[name] &&
      (
        Array.isArray(formik.errors) &&
        (formik.errors[index] as FormikErrors<InvoiceLineType>)?.[name]
      )
    )

  const getFormErrorMessage = (index: number, name: FormFieldName) => {
    // @ts-ignore
    const fieldErrors = formik.errors[index]

    return (
      !isFormFieldValid(index, name) && (
        <small className="p-error">{fieldErrors?.[name]}</small>
      )
    )
  }

  return (
    <LinesView
      formik={formik}
      invoiceLines={invoiceLines}
      isLoading={isLoading}
      hasLoadingRowId={hasLoadingRowId}
      handleSave={handleSave}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      editable={editable}
    />
  )
}
